import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from 'react-redux';
import Chart from "react-apexcharts";
import html2canvas from "html2canvas";
import {equals, groupBy, identity, keys, map, mergeAll, pathOr, pipe, sortBy} from 'ramda';
import {
  Box,
  Card,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  Tooltip, Chip
} from "@mui/material";
import CircleIcon from "@mui/icons-material/Circle";
import { http } from "../../../../../clients/http/http.client";
import { HTTP_ENDPOINTS, WEB_STORAGE_KEYS } from "../../../../../clients/clients.constants";
import Loader from "../../../../Inputs/Loader/Loader";
import { renderToString } from "react-dom/server";
import { DateTime } from "luxon";
import SocketClient from '../../../../../clients/socket.client';
import { setTimeZone } from "../../../../../store/timeZoneSlice";

import "./storeHours.css";
import { getYearMonthDay } from "../../../../../utils";
import { Download, Print } from "@mui/icons-material";
import { formatDateTimeInTimeZone } from "../../../../../helpers/useTimeZoneTime";
import ExportModal from "../../../../general/ExportModal";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {Link, useNavigate} from 'react-router-dom';
import DatePicker from "../../../../Inputs/DatePicker/DatePicker";
import {setActionsFrom, setActionsTo} from "../../../../../store/actionsDateFiltersSlice";
import {setSelectedDateAction} from "../../../../../store/actionsSelectedDateSlice";
import { selectSiteTypes } from "../../../../../store/siteTypeSlice";
import {WebStorageClient} from "../../../../../clients/webStorage.client";
import * as XLSX from "xlsx";
import Papa from "papaparse";
import {setStoreDateRange} from "../../../../../store/analyticsSelections";

/** Helper to parse the date from string into a JS Date */
const parseDate = (date) => {
  const targetDate = new Date(date);
  // Extract components
  const year = targetDate.getUTCFullYear();
  const month = targetDate.getUTCMonth();
  const day = targetDate.getUTCDate();
  const hours = targetDate.getUTCHours();
  const minutes = targetDate.getUTCMinutes();
  const seconds = targetDate.getUTCSeconds();

  // Convert to a JS Date using Luxon for consistency
  return DateTime.fromObject({
    year,
    month: month + 1,
    day,
    hour: hours,
    minute: minutes,
    second: seconds
  }).toJSDate();
};

const { ALERTS_STORE_HOURS_STATISTICS, STORE_SETTINGS } = HTTP_ENDPOINTS;

/** Custom Tooltip for the area chart */
const CustomTooltip = ({ data, timeZone }) => {
  const styles = {
    container: {
      width: "250px",
      color: "#fff",
    },
    heading: {
      backgroundColor: "#2D3748",
      padding: "10px",
      borderRadius: "8px 8px 0 0",
      fontWeight: 900,
      fontSize: "12px",
    },
    body: {
      backgroundColor: "rgba(45, 55, 72, .8)",
      padding: "5px 10px",
      borderRadius: "0 0 8px 8px",
    },
    values: {
      marginBottom: "10px",
      gap: "10px",
      display: "flex"
    },
    valueRow: {
      display: "flex",
      gap: '5px',
      marginBottom: "5px",
    },
    text: {
      fontSize: "12px",
    },
  };

  // Format time in UTC for the tooltip
  const dt = DateTime.fromISO(data?.time).setZone('utc');
  const formattedTime = dt.toFormat('MM/dd/yyyy hh:mm:ss a');

  return (
    <Box style={styles.container}>
      <Box style={styles.heading}>
        <Typography style={styles.text}>{formattedTime}</Typography>
      </Box>
      {/* Example of "Shopping Cart Large" breakdown */}
      <Box key='Shopping Cart Large' style={{ ...styles.body, borderRadius: '0' }}>
        <Typography style={styles.text}><strong>Shopping Cart Large</strong></Typography>
        <Box style={styles.values}>
          <Box style={styles.valueRow}>
            <Typography style={styles.text}>In use:</Typography>
            <Typography style={styles.text}>{data?.['Shopping Cart Large']?.inUse}</Typography>
          </Box>
          <Box style={styles.valueRow}>
            <Typography style={styles.text}>Out of store:</Typography>
            <Typography style={styles.text}>{data?.['Shopping Cart Large']?.outOfStore}</Typography>
          </Box>
          <Box style={styles.valueRow}>
            <Typography style={styles.text}>Carts zone:</Typography>
            <Typography style={styles.text}>{data?.['Shopping Cart Large']?.cartZone}</Typography>
          </Box>
        </Box>
      </Box>
      {/* Example of "Shopping Cart Small Two-Tier" breakdown */}
      <Box key='Shopping Cart Small Two-Tier' style={{ ...styles.body, borderRadius: '0' }}>
        <Typography style={styles.text}><strong>Shopping Cart Small Two-Tier</strong></Typography>
        <Box style={styles.values}>
          <Box style={styles.valueRow}>
            <Typography style={styles.text}>In use:</Typography>
            <Typography style={styles.text}>{data?.['Shopping Cart Small Two-Tier']?.inUse}</Typography>
          </Box>
          <Box style={styles.valueRow}>
            <Typography style={styles.text}>Out of store:</Typography>
            <Typography style={styles.text}>{data?.['Shopping Cart Small Two-Tier']?.outOfStore}</Typography>
          </Box>
          <Box style={styles.valueRow}>
            <Typography style={styles.text}>Carts zone:</Typography>
            <Typography style={styles.text}>{data?.['Shopping Cart Small Two-Tier']?.cartZone}</Typography>
          </Box>
        </Box>
      </Box>

      {/* Final summary row with circles: In / Out / Repair */}
      <Box style={styles.body}>
        <Box style={styles.values}>
          <Box style={{ display: "flex", alignItems: "center", gap: "5px" }}>
            <CircleIcon style={{ color: "#89D8C2" }} />
            <Typography style={styles.text}>{data.inUse}</Typography>
          </Box>
          <Box style={{ display: "flex", alignItems: "center", gap: "5px" }}>
            <CircleIcon style={{ color: "#E89585" }} />
            <Typography style={styles.text}>{data.outOfStore}</Typography>
          </Box>
          <Box style={{ display: "flex", alignItems: "center", gap: "5px" }}>
            <CircleIcon style={{ color: "#B6CFF1" }} />
            <Typography style={styles.text}>{data.cartZone}</Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

const StoreHoursCard = () => {
  const initialSelectedDate = WebStorageClient.getAlertSelectedDate({ forReducer: false });
  const navigate = useNavigate();
  const [selectedDate, setSelectedDate] = useState(initialSelectedDate);
  const [storeHoursData, setStoreHoursData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [chartType, setChartType] = useState("area");
  const [chartKey, setChartKey] = useState(0);
  const [minCartsInsideSmall, setMinCartsInsideSmall] = useState(null);
  const [minCartsInsideLarge, setMinCartsInsideLarge] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);
  const [isExporting, setIsExporting] = useState(false);
  const [generatedFiles, setGeneratedFiles] = useState(null);
  const [fileGenerationLoading, setIsFileGenerationLoading] = useState(false);
  const [annotations, setAnnotations] = useState({ yaxis: [] });
  const [error, setError] = useState(null);
  const [seriesVisibility, setSeriesVisibility] = useState({
    "In use": false,
    "Out of store": true,
    "Carts zone": false,
  });

  const [timeFrame, setTimeFrame] = useState("30");
  const [peakUsage, setPeakUsage] = useState("snapshot");
  const [siteIds, setSiteIds] = useState([]);

  const [selectOpen, setSelectOpen] = useState(false);
  const [showTooltips, setShowTooltips] = useState(true);
  const tooltipTimeoutRef = useRef(null);

  const getFileName = () => {
    if (chartType === "area") {
      return `carts_continuous_${Date.now()}`
    } else {
      return `carts_aggregated_${peakUsage}_${timeFrame}_${Date.now()}`
    }
  }

  const alertsSelectedDate = useSelector((state) => state.actionsSelectedDate);
  const siteIdStore = useSelector((state) => state.siteId);
  const siteName = useSelector((state) => state.siteName);
  const timeZone = useSelector((state) => state.timeZone.timeZone);
  const sites = useSelector(selectSiteTypes);

  const dispatch = useDispatch();
  const socket = SocketClient.getSocket();
  const user = useSelector((state) => state.user);

  /** Grouping example function - not necessarily used but kept from original */
  const groupData = (data, range, key) => {
    const result = [];
    let temp = {};

    const getPeriodKey = (time, range) => {
      switch (range) {
        case '3hours':
          return time.startOf('hour').minus({ hours: time.hour % 3 }).toFormat('yyyy-MM-dd HH:mm');
        case '20hours':
          return time.startOf('hour').minus({ hours: time.hour % 20 }).toFormat('yyyy-MM-dd HH:mm');
        case '1day':
          return time.startOf('day').toFormat('yyyy-MM-dd');
        case '1month':
          return time.startOf('month').toFormat('yyyy-MM');
        default:
          return time.startOf('hour').toFormat('yyyy-MM-dd HH');
      }
    };

    const groupedStoreHours = groupBy((sh) => getPeriodKey(DateTime.fromISO(sh.time), range))(data);

    const sortedKeys = pipe(keys, sortBy(identity))(groupedStoreHours);
    const sortedData = pipe(
      map((key) => ({ [key]: groupedStoreHours[key] })),
      mergeAll
    )(sortedKeys);

    for (let periodKey in sortedData) {
      const values = sortedData[periodKey].map((sh) => sh[key]);
      const max = Math.max(...values);
      const min = Math.min(...values);
      const firstValue = values[0];
      const lastValue = values[values.length - 1];

      result.push({ x: periodKey, y: [firstValue, max, min, lastValue] });
    }

    return result;
  };

  /** Build the query params based on date & site ID */
  const buildStoreHoursQueryParams = () => {
    let params = new URLSearchParams();

    if (alertsSelectedDate.selectedDate !== 'null' && alertsSelectedDate.selectedDate) {
      params.append('date', alertsSelectedDate.selectedDate);
    }

    if (siteIdStore.siteId) {
      params.append('siteId', siteIdStore.siteId);
    }

    if (chartType === 'bar') {
      params.append('timeFrame', timeFrame);
      params.append('peakUsage', peakUsage);
    }

    // If you need timeZone param, you can add it here
    // params.append('timezone', timeZone || "UTC")

    return params.toString() ? `?${params.toString()}` : '';
  };

  useEffect(() => {
    socket.emit('setSiteId', { siteId: siteIdStore.siteId });
  }, [socket, siteIdStore.siteId]);

  useEffect(() => {
    socket.emit('setTimeZoneSocket', { timezone: timeZone });
  }, [socket, timeZone]);

  const fetchStoreHoursData = async () => {
    setLoading(true);

    try {
      let filters = buildStoreHoursQueryParams();

      if (alertsSelectedDate.selectedDate && alertsSelectedDate.selectedDate !== 'null') {
        const date = DateTime.fromISO(alertsSelectedDate.selectedDate).toUTC();
        if (date.isValid) {
          socket.emit('enableStoreHoursSocket', { disableStoreHours: true });
        } else {
          socket.emit('enableStoreHoursSocket', { disableStoreHours: false });
        }
      } else {
        socket.emit('enableStoreHoursSocket', { disableStoreHours: false });

        if (chartType === 'bar') {
          socket.emit('setTimeFrameSocket', { timeFrame });
          socket.emit('setPeakUsageSocket', { peakUsage });
        } else {
          socket.emit('setTimeFrameSocket', { timeFrame: '' });
          socket.emit('setPeakUsageSocket', { peakUsage: '' });
        }
      }

      if (siteIdStore?.siteId) {
        const storeSettingsResponse = await http.client.get(
          `${STORE_SETTINGS}/${siteIdStore.siteId}`
        );
        setMinCartsInsideSmall(storeSettingsResponse.MinCartsInsideSmallCart);
        setMinCartsInsideLarge(storeSettingsResponse.MinCartsInsideLargeCart);
        dispatch(setTimeZone(storeSettingsResponse.TimeZone));
        socket.emit('setTimeZoneSocket', { timezone: storeSettingsResponse.TimeZone });
      } else {
        setMinCartsInsideSmall(null);
        setMinCartsInsideLarge(null);
        dispatch(setTimeZone(null));
      }

      const response = await http.client.get(
        `${ALERTS_STORE_HOURS_STATISTICS}${filters}`
      );

      // Reformat time if needed
      const data = response?.length
        ? response.map((sh) => ({
          ...sh,
          time: new Date(sh.time).toLocaleString("en-IT", {
            hour12: false,
          })
        }))
        : [];

      setStoreHoursData(response);
      setLoading(false);
      setChartKey((prevState) => prevState++);
    } catch (error) {
      setLoading(false);
      setMinCartsInsideSmall(null);
      setMinCartsInsideLarge(null);
      setError(true);
      console.error("Error fetching store hours data:", error);
    }
  };

  /** Fetch store hours data (and store settings) whenever date/site/timeZone changes */
  useEffect(() => {
    fetchStoreHoursData();
  }, [alertsSelectedDate.selectedDate, siteIdStore, timeZone, dispatch, socket, chartType, timeFrame, peakUsage]);

  const handleSelectedDateChange = (dateStr) => {
    console.log('dateStr',dateStr);
    if (dateStr) {
      // dateStr comes in "yyyy-MM-dd" format from DatePicker.
      setSelectedDate(dateStr);
      localStorage.setItem(WEB_STORAGE_KEYS.ALERTS_SELECTED_DATE, JSON.stringify(dateStr));
      dispatch(setSelectedDateAction(dateStr));
    } else {
      setSelectedDate(null);
      localStorage.setItem(WEB_STORAGE_KEYS.ALERTS_SELECTED_DATE, JSON.stringify(null));
      dispatch(setSelectedDateAction(null));
    }
  };

  /** Update chart's horizontal annotation line for "Min Carts in use" */
  useEffect(() => {
    if (minCartsInsideSmall !== null && minCartsInsideLarge !== null) {
      setAnnotations({
        yaxis: [
          {
            y: minCartsInsideSmall + minCartsInsideLarge,
            borderColor: 'rgba(255,0,0,0.65)',
            label: {
              borderColor: 'rgba(255,0,0,0.65)',
              style: {
                color: '#fff',
                background: 'rgba(255,0,0,0.45)',
              },
              text: 'Min Carts In use',
            },
          },
        ],
      });
    } else {
      setAnnotations({ yaxis: [] });
    }
  }, [minCartsInsideSmall, minCartsInsideLarge]);

  /** Listen to socket for store hours updates in real-time */
  useEffect(() => {
    const handleStoreHoursUpdate = (socketData) => {
      if (chartType === 'area') {
        setStoreHoursData((prevState) => {
          const prevStateMap = new Map(
            prevState.map((item) => [
              `${item.Site_Key}-${item.id}-${item.Date}-${item.Time}`,
              item
            ])
          );

          socketData.forEach((currentSh) => {
            const key = `${currentSh.Site_Key}-${currentSh.id}-${currentSh.Date}-${currentSh.Time}`;
            if (prevStateMap.has(key)) {
              const prevSh = prevStateMap.get(key);
              if (JSON.stringify(prevSh) !== JSON.stringify(currentSh)) {
                prevStateMap.set(key, { ...prevSh, ...currentSh });
              }
            } else {
              prevStateMap.set(key, currentSh);
            }
          });

          return Array.from(prevStateMap.values()).map((sh) => ({
            ...sh,
            time: sh.time,
          }));
        });
      }

      if (chartType === 'bar') {
        setStoreHoursData(socketData)
      }
    };

    socket.on('storeHours', handleStoreHoursUpdate);
    return () => {
      socket.off('storeHours', handleStoreHoursUpdate);
    };
  }, [storeHoursData, timeZone, socket, timeFrame, peakUsage, chartType]);

  useEffect(() => {
    (async () => {
      try {
        const res = await http.client.get(HTTP_ENDPOINTS.EQUIPMENT_TYPES);
        const siteIds = pathOr([], ['siteIds'], res);
        setSiteIds(siteIds);
        // You can then update state or perform other actions with siteIds
      } catch (err) {
        console.error("Error fetching equipment types:", err);
      }
    })();
  }, []);


  const seriesNames = ["In use", "Out of store", "Carts zone"];

  /** Area chart config for "Continuous" view */
  const chartData = {
    series: [
      {
        name: "In use",
        hidden: !seriesVisibility["In use"], // if visibility is false, hidden is true
        data: storeHoursData.map((d) => ({ x: parseDate(d?.time), y: d?.inUse })),
      },
      {
        name: "Out of store",
        hidden: !seriesVisibility["Out of store"],
        data: storeHoursData.map((d) => ({ x: parseDate(d?.time), y: d?.outOfStore })),
      },
      {
        name: "Carts zone",
        hidden: !seriesVisibility["Carts zone"],
        data: storeHoursData.map((d) => ({ x: parseDate(d?.time), y: d?.cartZone })),
      },
    ],
    options: {
      chart: {
        type: 'area',
        height: 270,
        zoom: { enabled: true },
        toolbar: {
          show: true,
          offsetX: 0,
          offsetY: 0,
          tools: {
            download: false,
            selection: true,
            zoom: false,
            zoomin: false,
            zoomout: false,
            pan: false,
            reset: false,
          },
          // export: {
          //   csv: {
          //     filename: undefined,
          //     columnDelimiter: ',',
          //     headerCategory: 'Date',
          //     headerValue: 'value',
          //     categoryFormatter(x) {
          //       const dt = DateTime.fromJSDate(x).setZone(timeZone || 'local');
          //       return dt.toFormat('MM/dd/yyyy hh:mm:ss a')
          //     },
          //     valueFormatter(y) {
          //       return y
          //     }
          //   },
          // }
        },
        animations: { enabled: false },
        events: {
          // Handle legend clicks to update seriesVisibility state.
          legendClick: (chartContext, seriesIndex, config) => {
            const seriesName = seriesNames[seriesIndex];

            setSeriesVisibility((prev) => ({
              ...prev,
              [seriesName]: !prev[seriesName],
            }));
            // Return false to override the default toggle behavior.
            return false;
          },
        },
      },
      noData: {
        text: "No data",
        align: 'center',
        verticalAlign: 'middle',
        offsetY: -28,
        style: {
          color: '#1B3665',
          fontSize: '20px',
          zIndex: 999999
        }
      },
      xaxis: {
        type: "datetime",
        min: DateTime.fromObject({
          ...getYearMonthDay(alertsSelectedDate.selectedDate),
          hour: 8,
          minute: 30
        }).toMillis(),
        max: DateTime.fromObject({
          ...getYearMonthDay(alertsSelectedDate.selectedDate),
          hour: 22,
          minute: 30
        }).toMillis(),
        tickAmount: 27,
        labels: {
          show: true,
          datetimeUTC: false,
          formatter: (value, timestamp) => {
            return DateTime.fromMillis(timestamp).toFormat("hh:mm a");
          },
          style: { fontSize: "12px", color: "#4A5568" }
        },
        tooltip: {
          enabled: true,
          formatter: (val) => DateTime.fromMillis(val).toFormat("hh:mm a"),
        },
      },
      yaxis: {
        labels: {
          style: {
            fontSize: "12px",
            fontFamily: "Arial",
            color: "#4A5568"
          },
        },
      },
      annotations,
      tooltip: {
        custom:
          chartType === 'area'
            ? ({ series, seriesIndex, dataPointIndex, w }) => {
              const data = storeHoursData[dataPointIndex];
              return renderToString(<CustomTooltip data={data} timeZone={timeZone} />);
            }
            : ({ series, seriesIndex, dataPointIndex, w }) => {
              // Aggregated (candlestick) tooltip logic if you ever use groupData()
              const seriesData = w.globals.initialSeries[seriesIndex].data[dataPointIndex];
              const open = seriesData.y[0];
              const high = seriesData.y[1];
              const low = seriesData.y[2];
              const close = seriesData.y[3];
              const name = w.globals.initialSeries[seriesIndex].name;
              const periodKey = seriesData.x;

              return renderToString(
                <Box style={{ width: "150px", color: "#fff" }}>
                  <Box
                    style={{
                      backgroundColor: "#2D3748",
                      padding: "10px",
                      borderRadius: "8px 8px 0 0",
                      fontWeight: 900,
                      fontSize: "12px"
                    }}
                  >
                    <Typography style={{ fontSize: "12px" }}>
                      {`${name} ${periodKey}`}
                    </Typography>
                  </Box>
                  <Box
                    style={{
                      backgroundColor: "rgba(45, 55, 72, .8)",
                      padding: "10px",
                      borderRadius: "0 0 8px 8px"
                    }}
                  >
                    <Typography style={{ fontSize: "12px" }}>Open: {open}</Typography>
                    <Typography style={{ fontSize: "12px" }}>High: {high}</Typography>
                    <Typography style={{ fontSize: "12px" }}>Low: {low}</Typography>
                    <Typography style={{ fontSize: "12px" }}>Close: {close}</Typography>
                  </Box>
                </Box>
              );
            }
      },
      colors: ["#89D8C2", "#E89585", "#B6CFF1"],
      fill: {
        type: "gradient",
        gradient: {
          shadeIntensity: 0,
          opacityFrom: 0,
          opacityTo: 0,
          stops: [0, 100],
        },
      },
      stroke: {
        curve: 'smooth',
        width: 2,
      },
      dataLabels: { enabled: false },
      markers: { size: 0 },
      legend: {
        show: true,
        position: "bottom",
        horizontalAlign: "start",
        fontSize: "12px",
        labels: { colors: "#4A5568" },
        markers: {
          width: 10,
          height: 10,
          radius: 2,
        },
      },
    },
  };

  function generateTimeRanges(interval) {
    // 9:30 AM in total minutes from midnight => 9 * 60 + 30 = 570
    const start = 8 * 60 + 30;
    // 9:30 PM in total minutes from midnight => 21 * 60 + 30 = 1290
    const end = 22 * 60 + 30;

    const times = [];

    for (let t = start; t <= end; t += interval) {
      // Convert total minutes back to 24-hour time
      const hour24 = Math.floor(t / 60);
      const minute = t % 60;

      // Determine AM/PM
      const period = hour24 >= 12 ? "PM" : "AM";

      // Convert to 12-hour format
      let hour12 = hour24 % 12;
      if (hour12 === 0) {
        hour12 = 12; // e.g. 0 => 12 AM or 12 PM
      }

      // Format minutes with leading zero if needed
      const minuteStr = minute < 10 ? `0${minute}` : minute;

      // Push string like "9:30AM" or "10:00PM" (no space, matching your original style)
      times.push(`${hour12}:${minuteStr}${period}`);
    }

    return times;
  }

  /** Mock bar chart config for "Aggregated" view */
  const mockOptions = {
    series: [
      {
        name: 'In use',
        data: storeHoursData.map((d) => d?.inUse),
      },
      {
        name: 'Out of store',
        data: storeHoursData.map((d) => d?.outOfStore),
      },
      {
        name: 'Carts zone',
        data: storeHoursData.map((d) => d?.cartZone),
      }
    ],
    options: {
      chart: {
        type: 'bar',
        height: 300,
        toolbar: {
          show: true,
          offsetX: 0,
          offsetY: 0,
          tools: {
            download: false,
            selection: true,
            zoom: false,
            zoomin: false,
            zoomout: false,
            pan: false,
            reset: false,
          },
          // export: {
          //   csv: {
          //     filename: undefined,
          //     columnDelimiter: ',',
          //     headerCategory: 'Date',
          //     headerValue: 'value',
          //     categoryFormatter(x) {
          //       return x
          //     },
          //     valueFormatter(y) {
          //       console.log(y);
          //       return y
          //     }
          //   },
          // }
        },
      },
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: '55%',
          borderRadius: 5
        },
      },
      dataLabels: { enabled: false },
      stroke: {
        show: true,
        width: 2,
        colors: ['transparent']
      },
      colors: ["#89D8C2", "#E89585", "#B6CFF1"],
      xaxis: {
        categories: generateTimeRanges(parseInt(timeFrame, 10)),
        labels: { style: { fontSize: '12px', color: '#4A5568' } },
      },
      yaxis: {
        labels: { style: { fontSize: '12px', color: '#4A5568' } },
      },
      fill: { opacity: 1 },
      legend: {
        show: true,
        position: "bottom",
        horizontalAlign: "start",
        fontSize: "12px",
        labels: { colors: "#4A5568" },
        markers: {
          width: 10,
          height: 10,
          radius: 2,
        },
      },
    },
  };

  /**
   * Handle downloads (CSV, XLSX) for store hours statistics
   *
   * @param {'CSV'|'Excel'} fileType  – type of file to generate
   * @param {string}         fileName  – base file name (without extension)
   */
  const handleAllStatisticsDownload = (fileType, fileName) => {
    console.log(sites);
    try {
      // 1) Extract only the fields we care about
      const dataToExport = storeHoursData.map(item => ({
        cartZone:    item.cartZone,
        inUse:       item.inUse,
        outOfStore:  item.outOfStore,
        site:        sites?.find((site) => site?.value === item?.Site_Key)?.name || '',
        Date:        item.Date,
        Time:        item.Time.split('.')[0]
      })).filter((item) => item.cartZone !== 0 && item.inUse !== 0 && item.outOfStore !== 0);

      // 2) CSV export
      if (fileType === 'CSV') {
        const csvString = Papa.unparse(dataToExport);
        const blob      = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
        const url       = URL.createObjectURL(blob);
        const link      = document.createElement('a');

        link.href        = url;
        link.setAttribute('download', `${fileName}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }
      // 3) Excel export
      else if (fileType === 'Excel') {
        // Create a sheet from our data
        const worksheet = XLSX.utils.json_to_sheet(dataToExport);
        const workbook  = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'StoreHours');

        // This will prompt the browser to download the .xlsx file
        XLSX.writeFile(workbook, `${fileName}.xlsx`);
      }
      else {
        console.warn(`Unsupported fileType "${fileType}".`);
      }
    }
    catch (error) {
      console.error('Error exporting store-hours statistics:', error);
    }
  };
  /**
   * Check if the file was generated successfully (server response)
   */
  const checkGeneratedFile = async () => {
    try {
      const email = user.user.preferred_username;
      const dataType = "carts_statistics";
      const res = await http.client.get(
        `${HTTP_ENDPOINTS.GENERATED_FILE}?dataType=${dataType}&email=${encodeURIComponent(email)}`
      );
      if (res.files && res.files.length > 0) {
        setGeneratedFiles(res.files);
      } else {
        setGeneratedFiles([]);
      }
    } catch (error) {
      console.error("Error checking generated file:", error);
      setGeneratedFiles([]);
    }
  };

  /** Open/Close the export modal */
  const openModal = async () => {
    setModalOpen(true);
    await checkGeneratedFile();
  };

  const onDownloadClick = () => {

  }


  const closeModal = () => {
    setModalOpen(false);
  };

  /** Handlers for Time Frame and Peak Usage <Select> */
  const handleTimeFrameChange = (event) => {
    setTimeFrame(event.target.value);
    // Re-fetch or filter logic if needed
  };
  const handlePeakUsageChange = (event) => {
    setPeakUsage(event.target.value);
    // Re-fetch or filter logic if needed
  };

  const chartBoxRef = useRef(null);

  // Function to capture the Box content and download as PNG
  // const handleDownloadChart = async () => {
  //   if (chartBoxRef.current) {
  //     try {
  //       // Use html2canvas to capture the Box element as a canvas
  //       const canvas = await html2canvas(chartBoxRef.current);
  //       // Convert the canvas to a PNG data URL
  //       const dataUrl = canvas.toDataURL("image/png");
  //       // Create a temporary link to trigger the download
  //       const link = document.createElement("a");
  //       link.href = dataUrl;
  //       link.download = `chart_${Date.now()}.png`;
  //       document.body.appendChild(link);
  //       link.click();
  //       document.body.removeChild(link);
  //     } catch (error) {
  //       console.error("Error capturing chart:", error);
  //     }
  //   } else {
  //     console.error("Chart container not available.");
  //   }
  // };

  const handleDownloadChart = async () => {
    if (chartBoxRef.current) {
      try {
        // Capture the Box element as a canvas using html2canvas
        const canvas = await html2canvas(chartBoxRef.current);
        // Convert the canvas to a PNG data URL
        const dataUrl = canvas.toDataURL("image/png");

        // Get the current date formatted as YYYYMMDD_HHmmss using Luxon
        const currentDate = DateTime.now().toFormat("yyyyMMdd_HHmmss");

        const site = siteIds.find(value => value.id === siteIdStore.siteId);
        const siteName = site ? site.name : "Unknown";

        // Construct the file name using chartType, siteName, and current date
        // Ensure you have the variable `siteName` available in your scope.
        const fileName = `${chartType}_${siteName}_${currentDate}.png`;

        // Create a temporary link to trigger the download
        const link = document.createElement("a");
        link.href = dataUrl;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (error) {
        console.error("Error capturing chart:", error);
      }
    } else {
      console.error("Chart container not available.");
    }
  };


  const getModeTooltip = (mode) => {
    switch (mode) {
      case 'low':
        return 'Shows the lowest cart count reached during the block.';
      case 'high':
        return 'Shows the highest cart count reached during the block.';
      case 'total':
        return 'Adds up all cart readings in the block.';
      case 'snapshot':
        return 'Takes the cart count exactly at the start of the block.';
      default:
        return '';
    }
  }

  const handleSelectOpen = () => {
    setSelectOpen(true);
    setShowTooltips(false);
    if (tooltipTimeoutRef.current) {
      clearTimeout(tooltipTimeoutRef.current);
    }
  };

  const handleSelectClose = () => {
    setSelectOpen(false);

    // Clear any existing timeout
    if (tooltipTimeoutRef.current) {
      clearTimeout(tooltipTimeoutRef.current);
    }

    // Use a timeout to delay re-enabling the parent tooltip
    tooltipTimeoutRef.current = setTimeout(() => {
      setShowTooltips(true); // Re-enable parent tooltip after a delay
    }, 300); // 300ms delay to prevent tooltip from showing too quickly
  };

  // Additional mouse enter/leave handlers for the FormControl
  const handleFormControlMouseEnter = () => {
    if (!selectOpen) {
      setShowTooltips(true);
    }
  };

  const handleFormControlMouseLeave = () => {
    setShowTooltips(false);
  };

  // Clean up the timeout on unmount
  useEffect(() => {
    return () => {
      if (tooltipTimeoutRef.current) {
        clearTimeout(tooltipTimeoutRef.current);
      }
    };
  }, []);

  const handleWereInUseClick = () => {
    // Parse the selected date and set to start/end of day in the specified time zone
    const date = DateTime.fromISO(selectedDate);
    if (!date.isValid) {
      console.error('Invalid selectedDate:', selectedDate);
      return;
    }

    const startDate = date.startOf('day').toISO();
    const endDate = date.endOf('day').toISO();

    console.log('startDate', startDate);
    console.log('endDate', endDate);

    // Dispatch to Redux store
    dispatch(setStoreDateRange({ start: startDate, end: endDate }));

    // Navigate to the asset-tracking page with the were-in-use view
    navigate('/asset-tracking?view=were-in-use');
  };

  return (
    <Card
      className="storeHoursCard"
      sx={{
        p: 2,
        borderRadius: "12px",
        boxShadow: "0px 4px 40px 0px rgba(226, 236, 249, 0.60)",
        backgroundColor: "#fff",
        flex: 1
      }}
    >
      {
        !siteIdStore.siteId ? (
            <Card sx={{ boxShadow: 'none', display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px', borderRadius: '12px' }}>
              <Typography sx={{ fontWeight: 600, fontSize: "16px", color: "#1B3665" }}>Select Site to view carts status</Typography>
            </Card>
          ) :
          <>
            {/* HEADER ROW */}
            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
              {/* LEFT CONTROLS: Title, Toggle Buttons, Time Frame, Peak Usage, Export */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography
                  sx={{
                    fontSize: "22px",
                    lineHeight: "22px",
                    fontWeight: 600,
                    color: "#1B3665",
                  }}
                >
                  Carts Status
                </Typography>

                {/* Aggregated vs Continuous Toggle */}
                <ToggleButtonGroup
                  size="small"
                  color="primary"
                  exclusive
                  value={chartType}
                  onChange={(event, newValue) => {
                    if (newValue) {
                      setLoading(true);
                      setChartType(newValue);
                      setChartKey((prevKey) => prevKey + 1);
                    }
                  }}
                  sx={{
                    border: 'none',
                    borderRadius: 'none',
                    gap: '10px'
                  }}
                >
                  <Tooltip 
                    placement="top"
                    className="nav-tooltip"
                    title="Groups data into larger time blocks so you can compare summary values instead of every single reading."
                  >
                    <ToggleButton
                      value="bar"
                      sx={{
                        fontSize: '12px',
                        textTransform: 'none',
                        padding: '4px 10px',
                        border: 'none',
                        color: '#5A5A5A',
                        background: '#F6F6F6',
                        borderRadius: '5px!important',
                        '&.Mui-selected': {
                          background: '#1B3665',
                          color: '#fff',
                          '&:hover': {
                            backgroundColor: '#1B3665',
                          },
                        },
                      }}
                    >
                      Aggregated
                    </ToggleButton>
                  </Tooltip>
                  <Tooltip 
                    placement="top"
                    className="nav-tooltip"
                    title="Shows real-time cart counts at every time point. X-axis is clock time; Y-axis is number of carts."
                  >
                    <ToggleButton
                      value="area"
                      sx={{
                        fontSize: '12px',
                        textTransform: 'none',
                        padding: '4px 10px',
                        border: 'none',
                        color: '#5A5A5A',
                        background: '#F6F6F6',
                        borderRadius: '5px!important',
                        '&.Mui-selected': {
                          background: '#1B3665',
                          color: '#fff',
                          '&:hover': {
                            backgroundColor: '#1B3665',
                          },
                        },
                      }}
                    >
                      Continuous
                    </ToggleButton>
                  </Tooltip>
                </ToggleButtonGroup>
                <Box>
                  <DatePicker
                    value={selectedDate}
                    onChange={handleSelectedDateChange}
                  />
                </Box>
              </Box>
              {/* Download Button / Export Modal */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>

                {
                  chartType === "bar" && (
                    <>
                      {/* Time Frame Select */}
                      <Box>
                        <Tooltip 
                          placement="top"
                          className="nav-tooltip"
                          title="Choose how long each block is (15 min, 30 min, 1 hr). All readings within each block will be combined according to the selected mode."
                        >
                          <FormControl size="small" sx={{ minWidth: 120  }}>
                            <Select
                              value={timeFrame}
                              onChange={handleTimeFrameChange}
                              IconComponent={ExpandMoreIcon} // use a custom arrow if you like
                              sx={{
                                // The select "box"
                                backgroundColor: '#EEF4FE',
                                borderRadius: 2,
                                color: '#2F7CD6',
                                fontSize: 14,
                                '.MuiSelect-select': {
                                  padding: '5px 15px',
                                },

                                // Remove the default outline; only show on focus
                                '.MuiOutlinedInput-notchedOutline': {
                                  border: '1px solid #B6CFF1',
                                  boxShadow: '0px 0px 0px 2px rgba(182, 207, 241, 0.40)'
                                },
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                  border: '1px solid #B6CFF1',
                                },

                                // The arrow icon
                                '.MuiSelect-icon': {
                                  color: '#2F7CD6',
                                },
                              }}
                              MenuProps={{
                                PaperProps: {
                                  sx: {
                                    mt: 1,
                                    borderRadius: 2,
                                    boxShadow: '0px 4px 40px 0px rgba(226, 236, 249, 0.60)',
                                    // Style the MenuItems and add a bottom border
                                    '& .MuiMenuItem-root': {
                                      color: '#2F7CD6',
                                      fontSize: 14,
                                      borderBottom: '1px solid #E2E2E2',
                                      '&:last-of-type': {
                                        borderBottom: 'none',
                                      },
                                      '&:hover': {
                                        backgroundColor: '#EEF4FE',
                                      },
                                    },
                                  },
                                },
                              }}
                            >
                              <MenuItem value="15">15 min</MenuItem>
                              <MenuItem value="30">30 min</MenuItem>
                              <MenuItem value="60">60 min</MenuItem>
                            </Select>
                          </FormControl>
                        </Tooltip>
                      </Box>

                      {/* Peak Usage Select */}
                      <Box>
                        <Tooltip
                          placement="top"
                          className="nav-tooltip"
                          title={getModeTooltip(peakUsage)}
                          open={showTooltips ? undefined : false}
                        >
                          <FormControl
                            size="small"
                            sx={{ minWidth: 170 }}
                            onMouseEnter={handleFormControlMouseEnter}
                            onMouseLeave={handleFormControlMouseLeave}
                          >
                            <Select
                              value={peakUsage}
                              onChange={handlePeakUsageChange}
                              IconComponent={ExpandMoreIcon}
                              open={selectOpen}
                              onOpen={handleSelectOpen}
                              onClose={handleSelectClose}
                              sx={{
                                // The select "box"
                                backgroundColor: '#EEF4FE',
                                borderRadius: 2,
                                color: '#2F7CD6',
                                fontSize: 14,
                                '.MuiSelect-select': {
                                  padding: '5px 15px',
                                },

                                // Remove the default outline; only show on focus
                                '.MuiOutlinedInput-notchedOutline': {
                                  border: '1px solid #B6CFF1',
                                  boxShadow: '0px 0px 0px 2px rgba(182, 207, 241, 0.40)'
                                },
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                  border: '1px solid #B6CFF1',
                                },

                                // The arrow icon
                                '.MuiSelect-icon': {
                                  color: '#2F7CD6',
                                },
                              }}
                              MenuProps={{
                                PaperProps: {
                                  sx: {
                                    mt: 1,
                                    borderRadius: 2,
                                    boxShadow: '0px 4px 40px 0px rgba(226, 236, 249, 0.60)',
                                    // Style the MenuItems and add a bottom border
                                    '& .MuiMenuItem-root': {
                                      color: '#2F7CD6',
                                      fontSize: 14,
                                      borderBottom: '1px solid #E2E2E2',
                                      '&:last-of-type': {
                                        borderBottom: 'none',
                                      },
                                      '&:hover': {
                                        backgroundColor: '#EEF4FE',
                                      },
                                    },
                                  },
                                },
                              }}
                            >
                              <MenuItem value="low">
                                <Tooltip
                                  title={selectOpen ? getModeTooltip('low') : ''}
                                  placement="right"
                                  open={selectOpen ? undefined : false}
                                >
                                  <span>Peak Usage (Low)</span>
                                </Tooltip>
                              </MenuItem>
                              <MenuItem value="high">
                                <Tooltip
                                  title={selectOpen ? getModeTooltip('high') : ''}
                                  placement="right"
                                  open={selectOpen ? undefined : false}
                                >
                                  <span>Peak Usage (High)</span>
                                </Tooltip>
                              </MenuItem>
                              <MenuItem value="total">
                                <Tooltip
                                  title={selectOpen ? getModeTooltip('total') : ''}
                                  placement="right"
                                  open={selectOpen ? undefined : false}
                                >
                                  <span>Total</span>
                                </Tooltip>
                              </MenuItem>
                              <MenuItem value="snapshot">
                                <Tooltip
                                  title={selectOpen ? getModeTooltip('snapshot') : ''}
                                  placement="right"
                                  open={selectOpen ? undefined : false}
                                >
                                  <span>Snapshot</span>
                                </Tooltip>
                              </MenuItem>
                            </Select>
                          </FormControl>
                        </Tooltip>
                      </Box>
                    </>
                  )
                }

                <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
                  <Chip
                    label="Were in use"
                    variant="outlined"
                    onClick={handleWereInUseClick}
                    disabled={!selectedDate}
                    sx={{
                      fontSize: '14px',
                      fontWeight: 'bold',
                      color: '#1B3665',
                      borderColor: '#1B3665',
                      '& .MuiChip-deleteIcon': {
                        display: 'none'
                      },
                    }}
                  />
                </Box>

                <IconButton color="primary" onClick={openModal}>
                  <Download />
                </IconButton>

                <IconButton
                  color="primary"
                  onClick={handleDownloadChart}
                  disabled={loading}
                >
                  <Print />
                </IconButton>
                <ExportModal
                  open={modalOpen}
                  onClose={closeModal}
                  fileName={getFileName()}
                  handleDownloadAll={handleAllStatisticsDownload}
                  isMovements
                  dataType='cartsStatus'
                  progress={exportProgress}
                  generatedFiles={generatedFiles}
                  fileGenerationLoading={fileGenerationLoading}
                />
              </Box>
            </Box>

            {/* CHART RENDER */}
            <Box ref={chartBoxRef}>
              {loading ? (
                <Loader sx={{ height: '260px' }} />
              ) : (
                <>
                  {chartType === 'area' && (
                    <Chart
                      key={chartKey}
                      options={chartData.options}
                      series={chartData.series}
                      type='area'
                      height="390"
                    />
                  )}
                  {chartType === 'bar' && (
                    <Chart
                      key={chartKey}
                      options={mockOptions.options}
                      series={mockOptions.series}
                      type='bar'
                      height="390"
                    />
                  )}
                </>
              )}
            </Box>
          </>
      }
    </Card>
  );
};

export default StoreHoursCard;
